/* global AbortController, CustomEvent, HTMLElement, console, customElements, document, fetch, window */

(() => {
  const ELEMENT_NAME = 'deeply-trade-in-placement';
  const STYLE_ID = 'deeply-trade-in-placement-styles';
  const CATALOG_URL = '/apps/trade-in/api/v1/catalog';
  const LOOKUP_URL = '/apps/trade-in/api/v1/device-lookups';
  const ESTIMATE_URL = '/apps/trade-in/api/v1/estimates';
  const DEFAULT_SELL_URL = 'https://online.dipli360.com/idream/trade-in/steps/model';
  const DEFAULT_CURRENCY = 'PLN';
  const SELECT_PLACEHOLDER = 'Wybierz model';
  const YES_TEXT = 'Tak';
  const NO_TEXT = 'Nie';
  const IMEI_PATTERN = /^\d{15,17}$/;
  const TAC_PATTERN = /^\d{8}$/;
  const FLOW_STAGE_INITIAL = 'initial';
  const FLOW_STAGE_DEVICE = 'device';
  const FLOW_STAGE_BRAND = 'brand';
  const FLOW_STAGE_MODEL = 'model';
  const FLOW_STAGE_CONDITION = 'condition';
  const CONDITION_FIELDS = ['turnsOn', 'screenGood', 'hasFaults', 'aestheticGrade'];
  const DEFAULT_FALLBACK_DESCRIPTION = 'Sprawdź wartość swojego urządzenia i wykorzystaj ją na nowy sprzęt Apple.';

  class CatalogRequestError extends Error {
    constructor(status, payload) {
      super(`Catalog request failed with ${status}`);
      this.name = 'CatalogRequestError';
      this.status = status;
      this.payload = payload;
    }
  }

  const firstMoneyValue = (...values) => {
    for (const value of values) {
      const moneyValue = toMoneyValue(value);

      if (moneyValue !== '') return moneyValue;
    }

    return '';
  };

  const normalizeText = (value) =>
    String(value || '')
      .trim()
      .replace(/\s+/g, ' ');

  const toHandle = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

  const sortByLabel = (items) =>
    [...items].sort((left, right) => left.label.localeCompare(right.label, undefined, { sensitivity: 'base' }));

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const toMoneyValue = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }

    if (typeof value !== 'string') return '';

    const normalized = value.replace(/[^\d.,-]/g, '').replace(',', '.');
    const parsed = Number.parseFloat(normalized);

    return Number.isFinite(parsed) ? String(parsed) : '';
  };

  const toBoolean = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value !== 'string') return null;

    const normalized = value.trim().toLowerCase();

    if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
    if (['false', '0', 'no', 'n'].includes(normalized)) return false;

    return null;
  };

  const centsToMoneyValue = (value) => {
    const cents = typeof value === 'number' ? value : Number.parseFloat(value);
    return Number.isFinite(cents) ? toMoneyValue(cents / 100) : '';
  };

  const toCartPropertyCents = (value) => {
    const amount = Number.parseFloat(toMoneyValue(value));
    return Number.isFinite(amount) ? String(Math.round(amount * 100)) : '0';
  };

  const toCartButtonValue = (value) => {
    const amount = Number.parseFloat(toMoneyValue(value));
    return Number.isFinite(amount) ? amount.toFixed(2) : '0.00';
  };

  const formatMoney = (value, currency, locale) => {
    const amount = Number.parseFloat(value);
    const normalizedCurrency = String(currency || DEFAULT_CURRENCY).toUpperCase();

    if (!Number.isFinite(amount)) return '';

    if (normalizedCurrency === 'PLN') {
      try {
        const displayAmount = new Intl.NumberFormat(locale || document.documentElement.lang || 'en', {
          maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        }).format(amount);

        return `${displayAmount} ${String(locale || document.documentElement.lang || '').startsWith('pl') ? 'zł' : 'PLN'}`;
      } catch {
        return `${amount.toLocaleString()} PLN`;
      }
    }

    try {
      return new Intl.NumberFormat(locale || document.documentElement.lang || 'en', {
        style: 'currency',
        currency: normalizedCurrency,
        maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      }).format(amount);
    } catch {
      return `${normalizedCurrency}${amount.toLocaleString()}`;
    }
  };

  const getCatalogCategories = (payload) => (Array.isArray(payload?.categories) ? payload.categories : []);

  const flattenCatalogModels = (payload) =>
    getCatalogCategories(payload).flatMap((category) =>
      (Array.isArray(category?.brands) ? category.brands : []).flatMap((brand) =>
        (Array.isArray(brand?.models) ? brand.models : []).map((model) => ({
          id: String(model.id),
          label: normalizeText(model.label),
          modelName: normalizeText(model.modelName),
          capacity: normalizeText(model.capacity),
          picture: model.picture || '',
          categoryKey: String(category.key),
          categoryLabel: normalizeText(category.label),
          brandKey: String(brand.key),
          brand: normalizeText(brand.label),
        })),
      ),
    );

  const getCatalogAestheticGrades = (payload) => {
    const grades = Array.isArray(payload?.condition?.aestheticGrades) ? payload.condition.aestheticGrades : [];
    const normalized = grades
      .map((grade) => {
        if (!grade?.key || !grade?.label) return null;

        return {
          label: String(grade.label),
          state: String(grade.key),
        };
      })
      .filter(Boolean);

    return normalized;
  };

  const getCatalogConditionRequirements = (payload) => {
    const requirements = Array.isArray(payload?.condition?.requirements) ? payload.condition.requirements : [];

    return requirements
      .map((requirement) => {
        const field = String(requirement?.field || '');
        const visibleWhen = requirement?.visibleWhen;

        if (!CONDITION_FIELDS.includes(field) || !visibleWhen || typeof visibleWhen !== 'object') return null;

        const normalizedVisibleWhen = Object.fromEntries(
          Object.entries(visibleWhen).filter(
            ([answer, value]) => CONDITION_FIELDS.includes(answer) && typeof value === 'boolean',
          ),
        );

        return { field, visibleWhen: normalizedVisibleWhen };
      })
      .filter(Boolean);
  };

  const getHighestCatalogValue = (payload) => centsToMoneyValue(payload?.meta?.highestAmountMinor || 0);

  const renderHighestValueTemplate = (template, value, currency, locale, fallback = DEFAULT_FALLBACK_DESCRIPTION) => {
    const numericValue = Number.parseFloat(toMoneyValue(value));

    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      return hasHighestValueTemplate(template) ? fallback : template || fallback;
    }

    const displayValue = formatMoney(value, currency, locale);

    if (!template || !displayValue) return fallback;

    return template.replace(/[$€£]?\s*\{\{\s*highest_available_value\s*\}\}/g, displayValue);
  };

  const hasHighestValueTemplate = (template) => /\{\{\s*highest_available_value\s*\}\}/.test(template || '');

  const getRoutesRoot = () => window.Shopify?.routes?.root || '/';

  const parseProperties = (value) => {
    if (!value) return {};

    try {
      return JSON.parse(value);
    } catch {
      try {
        return JSON.parse(value.replace(/&quot;/g, '"'));
      } catch {
        return {};
      }
    }
  };

  const injectStyles = () => {
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      ${ELEMENT_NAME} {
        display: block;
      }

      .dipli-trade-in-modal[hidden] {
        display: none !important;
      }

      .dipli-trade-in-modal {
        align-items: flex-start;
        background: rgba(0, 0, 0, 0.48);
        display: flex;
        inset: 0;
        justify-content: center;
        overflow: auto;
        padding: 32px 16px;
        position: fixed;
        z-index: 2147483000;
      }

      .dipli-trade-in-modal__dialog {
        background: rgb(var(--color-background, 255, 255, 255));
        border: 0;
        border-radius: 24px;
        box-shadow: 0 20px 80px rgba(0, 0, 0, 0.24);
        color: #000;
        margin: 0 auto;
        max-width: 1120px;
        min-height: min(760px, calc(100vh - 64px));
        outline: none;
        padding: clamp(48px, 8vw, 96px);
        position: relative;
        width: min(88vw, 1120px);
      }

      .dipli-trade-in-modal__close {
        align-items: center;
        appearance: none;
        background: #f5f5f7;
        border: 0;
        border-radius: 999px;
        color: #6e6e73;
        cursor: pointer;
        display: inline-flex;
        font-size: 36px;
        font-weight: 600;
        height: 44px;
        justify-content: center;
        line-height: 1;
        padding: 0 0 4px;
        position: absolute;
        right: 24px;
        top: 24px;
        width: 44px;
      }

      .dipli-trade-in-modal__close:hover {
        color: #1d1d1f;
      }

      .dipli-trade-in-modal__close .fa-xmark {
        font-size: 18px;
        line-height: 1;
      }

      .dipli-trade-in-modal .trade-in__modal-content {
        margin: 0 auto;
        max-width: 920px;
      }

      .dipli-trade-in-modal .trade-in__modal-content-select .select {
        background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' fill='none' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath d='M15.1617 7.99998L14.3233 7.16162L10 11.4849L5.67674 7.16162L4.83838 7.99998L10 13.1616L15.1617 7.99998Z' fill='gray'/%3E%3C/svg%3E");
        background-position: calc(100% - 8px) center;
        background-repeat: no-repeat;
        background-size: 20px;
        min-height: 44px;
        width: 100%;
      }

      .dipli-trade-in-modal .trade-in__modal-content-select .select:focus {
        outline: none;
      }

      .dipli-trade-in-choice__input[disabled] + .dipli-trade-in-choice__label,
      ${ELEMENT_NAME} input[disabled] + label {
        cursor: not-allowed;
        opacity: 0.55;
      }

      ${ELEMENT_NAME} .dipli-trade-in__message {
        color: #595959;
        font-size: 14px;
        line-height: 20px;
        margin-top: 10px;
      }

      ${ELEMENT_NAME} .dipli-trade-in-cart-card {
        width: 100%;
      }

      ${ELEMENT_NAME} .dipli-trade-in-cart-selection {
        margin-top: 8px;
      }

      .trade-in__modal-content .dipli-trade-in-question {
        margin-top: 20px;
      }

      .trade-in__modal-content .dipli-trade-in-question__label {
        color: #000;
        font-size: 16px;
        line-height: 22px;
        margin: 0 0 10px;
      }

      .trade-in__modal-content .dipli-trade-in-question__options {
        display: flex;
        flex-flow: row wrap;
        gap: 8px;
      }

      .trade-in__modal-content .dipli-trade-in-choice {
        display: inline-flex;
        margin: 0;
        position: relative;
      }

      .trade-in__modal-content .dipli-trade-in-choice__input {
        appearance: none;
        clip: rect(0, 0, 0, 0);
        clip-path: inset(50%);
        border: 0;
        height: 1px;
        margin: -1px;
        opacity: 0;
        overflow: hidden;
        padding: 0;
        position: absolute;
        pointer-events: none;
        white-space: nowrap;
        width: 1px;
      }

      .trade-in__modal-content .dipli-trade-in-choice__label {
        align-items: center;
        background: #ffffff;
        border: 1.2px solid #d9d9d9;
        border-radius: 8px;
        box-sizing: border-box;
        color: #121212;
        cursor: pointer;
        display: inline-flex;
        font-family: "SF Pro Text", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        font-size: 15px;
        font-weight: 400;
        justify-content: center;
        line-height: 20px;
        min-height: 44px;
        min-width: 96px;
        padding: 10px 24px;
        text-align: center;
        transition: border-color 0.15s ease, box-shadow 0.15s ease;
      }

      .trade-in__modal-content .dipli-trade-in-choice__label:hover {
        border-color: #0071e3;
      }

      .trade-in__modal-content .dipli-trade-in-choice__label::before,
      .trade-in__modal-content .dipli-trade-in-choice__label::after {
        content: none !important;
        display: none !important;
      }

      .trade-in__modal-content .dipli-trade-in-choice.is-selected .dipli-trade-in-choice__label {
        background: #ffffff;
        border: 2px solid #0071e3;
        color: #121212;
        font-weight: 500;
      }

      .trade-in__modal-content .dipli-trade-in-value-panel[hidden] {
        display: none !important;
      }

      .dipli-trade-in-modal__dialog {
        border: 1.2px solid #0071e3;
        box-shadow: none;
        max-width: 868px;
        min-height: auto;
        padding: 0 0 76px;
        width: min(92vw, 868px);
      }

      .dipli-trade-in-modal__close {
        background: #f5f5f7;
        color: #121212;
        font-size: 26px;
        height: 36px;
        padding: 0 0 3px;
        right: 20px;
        top: 20px;
        width: 36px;
      }

      .dipli-trade-in-modal .dipli-trade-in-flow,
      .trade-in__modal-content.dipli-trade-in-flow {
        color: #121212;
        font-family: "SF Pro Text", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        margin: 0 auto;
        max-width: 714px;
        padding-top: 76px;
        /* Keep parent section alignment from leaking into the shared modal. */
        text-align: left;
      }

      @media only screen and (min-width: 1069px) {
        .modal:has(#modal-trade-in-modal) {
          --modal-overlay-width: 868px;
        }
      }

      .dipli-trade-in-flow .title {
        color: #121212;
        font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        font-size: 32px;
        font-weight: 600;
        letter-spacing: 0;
        line-height: 36px;
        margin: 0 0 30px;
        text-align: center;
      }

      .dipli-trade-in-flow .description {
        color: #030712;
        font-size: 16px;
        line-height: 20px;
        margin: 0 0 30px;
      }

      .dipli-trade-in-field,
      .dipli-trade-in-option-group,
      .dipli-trade-in-condition-flow {
        margin-top: 30px;
      }

      .dipli-trade-in-field--imei {
        margin-top: 0;
      }

      .dipli-trade-in-field__label,
      .dipli-trade-in-question__label {
        color: #000;
        display: block;
        font-size: 16px;
        font-weight: 600;
        line-height: 20px;
        margin: 0 0 12px;
      }

      .dipli-trade-in-imei-row {
        align-items: center;
        display: grid;
        gap: 14px;
        grid-template-columns: 1fr 135px;
      }

      .dipli-trade-in-input,
      .dipli-trade-in-select,
      .dipli-trade-in-dropdown__trigger {
        appearance: none;
        background-color: #fff;
        border: 0.8px solid #d9d9d9;
        border-radius: 4px;
        box-sizing: border-box;
        color: #000;
        font-size: 14px;
        height: 38px;
        line-height: 22px;
        padding: 8px;
        width: 100%;
      }

      .dipli-trade-in-select {
        background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' fill='none' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath d='M15.1617 7.99998L14.3233 7.16162L10 11.4849L5.67674 7.16162L4.83838 7.99998L10 13.1616L15.1617 7.99998Z' fill='gray'/%3E%3C/svg%3E");
        background-position: calc(100% - 8px) center;
        background-repeat: no-repeat;
        background-size: 20px;
        padding-right: 36px;
      }

      .dipli-trade-in-dropdown {
        box-sizing: border-box;
        position: relative;
        width: 100%;
      }

      .dipli-trade-in-dropdown__trigger {
        align-items: center;
        cursor: pointer;
        display: flex;
        font-family: "SF Pro Text", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        justify-content: space-between;
        text-align: left;
      }

      .dipli-trade-in-dropdown__trigger:disabled {
        cursor: not-allowed;
        opacity: 0.55;
      }

      .dipli-trade-in-dropdown__trigger:focus-visible,
      .dipli-trade-in-dropdown__option:focus-visible {
        outline: 2px solid #0071e3;
        outline-offset: 2px;
      }

      .dipli-trade-in-dropdown__value {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .dipli-trade-in-dropdown__value.is-placeholder {
        color: #1d1d1f;
      }

      .dipli-trade-in-dropdown__chevron {
        align-items: center;
        display: inline-flex;
        flex: 0 0 20px;
        height: 20px;
        justify-content: center;
        margin-left: 16px;
        width: 20px;
      }

      .dipli-trade-in-dropdown__chevron::before {
        border-color: #7a7a7a;
        border-style: solid;
        border-width: 0 1.6px 1.6px 0;
        content: "";
        display: block;
        height: 8px;
        transform: rotate(45deg) translateY(-2px);
        transition: transform 160ms ease;
        width: 8px;
      }

      .dipli-trade-in-dropdown.is-open .dipli-trade-in-dropdown__chevron::before {
        transform: rotate(225deg) translate(-2px, -2px);
      }

      .dipli-trade-in-dropdown__list {
        background: #fff;
        border: 0.8px solid #d9d9d9;
        border-radius: 4px;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        gap: 4px;
        margin-top: 4px;
        max-height: min(220px, 42vh);
        overflow-y: auto;
        padding: 8px;
        width: 100%;
      }

      .dipli-trade-in-dropdown__option {
        appearance: none;
        background: transparent;
        border: 0;
        box-sizing: border-box;
        color: #000;
        cursor: pointer;
        display: block;
        flex: 0 0 auto;
        font-family: "SF Pro Text", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        font-size: 14px;
        height: auto;
        line-height: 22px;
        min-height: 22px;
        overflow-wrap: anywhere;
        padding: 4px 8px;
        text-align: left;
        white-space: normal;
        width: 100%;
      }

      .dipli-trade-in-primary-action,
      .dipli-trade-in-chip {
        align-items: center;
        appearance: none;
        border-radius: 32px;
        cursor: pointer;
        display: inline-flex;
        font-family: "SF Pro Text", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        font-size: 14px;
        font-weight: 600;
        justify-content: center;
        letter-spacing: 0;
        line-height: 16px;
        min-height: 40px;
        text-align: center;
      }

      .dipli-trade-in-primary-action {
        background: #0071e3;
        border: 1.2px solid #0071e3;
        color: #fff;
        height: 40px;
        padding: 0 32px;
      }

      .dipli-trade-in-field--imei .dipli-trade-in-field__label {
        margin-bottom: 5px;
      }

      .dipli-trade-in-field--imei .dipli-trade-in-link {
        margin-top: 9px;
      }

      .dipli-trade-in-primary-action:disabled,
      .dipli-trade-in-chip:disabled {
        cursor: not-allowed;
        opacity: 0.55;
      }

      .dipli-trade-in-primary-action--large {
        min-width: 156px;
      }

      .dipli-trade-in-link {
        appearance: none;
        background: transparent;
        border: 0;
        color: #000;
        cursor: pointer;
        display: inline;
        font-size: 16px;
        line-height: 20px;
        margin-top: 10px;
        padding: 0;
        text-decoration: underline;
      }

      .dipli-trade-in-alert {
        background: rgba(227, 0, 0, 0.05);
        border-radius: 6px;
        color: #e30000;
        font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        font-size: 14px;
        line-height: 22px;
        margin: 24px 0 0;
        min-height: 46px;
        padding: 12px 22px;
      }

      .dipli-trade-in-chip-list {
        display: flex;
        flex-flow: row wrap;
        gap: 8px 10px;
      }

      .trade-in__modal-content .dipli-trade-in-question__options {
        display: flex;
        flex-flow: row wrap;
        gap: 10px 14px;
      }

      .dipli-trade-in-chip {
        background: #fff;
        border: 1.2px solid #0071e3;
        border-radius: 999px;
        color: #0071e3;
        font-family: "SF Pro Text", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        font-size: 13px;
        font-weight: 500;
        height: 36px;
        min-height: 36px;
        min-width: 100px;
        padding: 8px 16px;
        transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
      }

      .dipli-trade-in-chip.is-selected {
        background: #0071e3;
        border-color: #0071e3;
        color: #fff;
      }

      .dipli-trade-in-condition-flow__title {
        color: #000;
        font-family: "SF Pro Text", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        font-size: 16px;
        font-weight: 600;
        line-height: 20px;
        margin: 0 0 16px;
      }

      .trade-in__modal-content .dipli-trade-in-question {
        margin-top: 24px;
      }

      .dipli-trade-in-field--model .dipli-trade-in-field__label {
        margin-bottom: 12px;
      }

      .dipli-trade-in-dropdown-wrap {
        width: 100%;
      }

      .dipli-trade-in-value-panel {
        align-items: center;
        background: rgba(0, 113, 227, 0.05);
        border-radius: 12px;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        justify-content: center;
        margin-top: 30px;
        min-height: auto;
        padding: 32px 24px 28px;
        position: relative;
        text-align: center;
        width: 100%;
      }

      .dipli-trade-in-value-panel__title {
        color: #000;
        font-family: "SF Pro Text", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        font-size: 14px;
        font-weight: 500;
        line-height: 18px;
        margin: 0 0 6px;
        position: static;
        transform: none;
        width: 100%;
      }

      .dipli-trade-in-value-panel__amount {
        color: #0071e3;
        font-family: "SF Pro Display", "SF Pro Text", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        font-size: 38px;
        font-weight: 600;
        line-height: 44px;
        margin: 0 0 8px;
        position: static;
        transform: none;
        width: 100%;
      }

      .dipli-trade-in-value-panel__description {
        color: #4b5563;
        font-family: "SF Pro Text", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        font-size: 13px;
        line-height: 18px;
        margin: 0 0 20px;
        max-width: 480px;
        position: static;
        transform: none;
        width: 100%;
      }

      .dipli-trade-in-value-panel .dipli-trade-in-primary-action {
        align-items: center;
        background: #0071e3;
        border: none;
        border-radius: 999px;
        box-sizing: border-box;
        color: #fff;
        cursor: pointer;
        display: inline-flex;
        font-family: "SF Pro Text", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        font-size: 16px;
        font-weight: 600;
        height: 52px;
        justify-content: center;
        letter-spacing: 0;
        line-height: 20px;
        margin: 0 0 12px !important;
        max-width: 320px;
        min-height: 52px;
        min-width: 260px;
        padding: 0 32px;
        position: static;
        text-align: center;
        text-decoration: none !important;
        transform: none;
        transition: background-color 0.2s ease, box-shadow 0.2s ease;
        width: 100%;
      }

      .dipli-trade-in-value-panel .dipli-trade-in-primary-action:hover {
        background: #0077ed;
        box-shadow: 0 4px 14px rgba(0, 113, 227, 0.3);
      }

      .dipli-trade-in-value-panel .dipli-trade-in-primary-action:disabled {
        background: #e5e5ea;
        box-shadow: none;
        color: #8e8e93;
        cursor: not-allowed;
        opacity: 0.7;
      }

      .dipli-trade-in-value-panel .trade-in__learn-more-trigger {
        align-items: center;
        color: #0071e3;
        cursor: pointer;
        display: inline-flex;
        font-family: "SF Pro Text", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        font-size: 14px;
        line-height: 20px;
        margin: 4px 0 2px;
        text-decoration: none;
      }

      .dipli-trade-in-value-panel .trade-in__learn-more-trigger .underlined-text {
        text-decoration: underline;
      }

      .dipli-trade-in-value-panel .trade-in__learn-more-trigger .fa-angle-right,
      .dipli-trade-in-value-panel .trade-in__learn-more-trigger .billboard_icon {
        font-size: 12px;
        margin-left: 6px;
      }

      .dipli-trade-in-value-panel .dipli-trade-in-reset {
        background: transparent;
        border: 0;
        color: #6e6e73;
        cursor: pointer;
        display: inline-block;
        font-family: "SF Pro Text", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        font-size: 13px;
        line-height: 18px;
        margin: 8px 0 0;
        padding: 0;
        position: static;
        text-decoration: underline;
        transform: none;
        white-space: nowrap;
      }

      .dipli-trade-in-value-panel .dipli-trade-in-reset:hover {
        color: #1d1d1f;
      }

      @media screen and (max-width: 767px) {
        .dipli-trade-in-modal {
          align-items: flex-start;
          padding: 0;
        }

        .dipli-trade-in-modal__dialog {
          border-radius: 24px 24px 0 0;
          min-height: 100vh;
          min-height: 100svh;
          padding: 16px;
          width: 100%;
        }

        .dipli-trade-in-modal__close {
          right: 16px;
          top: 16px;
        }

        .dipli-trade-in-modal .dipli-trade-in-flow,
        .trade-in__modal-content.dipli-trade-in-flow {
          max-width: 290px;
          padding: 59px 0 0;
          width: 100%;
        }

        .dipli-trade-in-flow .title {
          font-size: 32px;
          line-height: 36px;
          margin-bottom: 16px;
        }

        .dipli-trade-in-flow .description {
          margin-bottom: 16px;
        }

        .dipli-trade-in-field__label,
        .trade-in__modal-content .dipli-trade-in-question__label {
          margin-bottom: 8px;
        }

        .dipli-trade-in-field,
        .dipli-trade-in-option-group,
        .dipli-trade-in-condition-flow,
        .trade-in__modal-content .dipli-trade-in-question {
          margin-top: 16px;
        }

        .dipli-trade-in-imei-row {
          gap: 9px;
          grid-template-columns: 1fr;
        }

        .dipli-trade-in-input,
        .dipli-trade-in-select,
        .dipli-trade-in-dropdown__trigger {
          height: 38px;
        }

        .dipli-trade-in-field--imei .dipli-trade-in-field__label {
          margin-bottom: 7px;
        }

        .dipli-trade-in-field--imei .dipli-trade-in-primary-action {
          height: 40px;
          margin-inline: auto;
          min-height: 40px;
          width: 135px;
        }

        .dipli-trade-in-field--imei .dipli-trade-in-link {
          margin-top: 9px;
        }

        .dipli-trade-in-chip {
          min-width: 0;
          padding-inline: 18px;
        }

        .dipli-trade-in-chip-list {
          gap: 7px 6px;
        }

        .trade-in__modal-content .dipli-trade-in-question__options {
          gap: 12px;
        }

        .dipli-trade-in-dropdown__list {
          max-height: 220px;
        }

        .dipli-trade-in-field--model .dipli-trade-in-field__label {
          margin-bottom: 9px;
        }

        .dipli-trade-in-field--aesthetic .dipli-trade-in-field__label {
          margin-bottom: 12px;
        }

        .trade-in__modal-content .dipli-trade-in-choice__label {
          flex: 1 1 calc(50% - 8px);
          min-width: 80px;
          padding-inline: 16px;
        }

        .dipli-trade-in-value-panel {
          margin-top: 20px;
          padding: 24px 16px 20px;
          width: 100%;
        }

        .dipli-trade-in-value-panel__title,
        .dipli-trade-in-value-panel__amount,
        .dipli-trade-in-value-panel__description {
          width: 100%;
        }

        .dipli-trade-in-value-panel .dipli-trade-in-primary-action {
          max-width: 100%;
          min-width: 0;
          width: 100%;
        }

        .dipli-trade-in-primary-action--large {
          min-width: 0;
        }
      }
    `;
    document.head.appendChild(style);
  };

  class DeeplyTradeInPlacement extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.uid = `${ELEMENT_NAME}-${Math.random().toString(36).slice(2)}`;
      this.abortController = new AbortController();
      this.products = [];
      this.catalogCategories = [];
      this.catalogVersion = '';
      this.catalogCurrency = DEFAULT_CURRENCY;
      this.selectedProduct = null;
      this.selectedCategory = '';
      this.selectedBrand = '';
      this.entryMode = 'imei';
      this.flowStage = FLOW_STAGE_INITIAL;
      this.openDropdown = '';
      this.imeiValue = '';
      this.imeiError = '';
      this.imeiLookupId = 0;
      this.lookupCandidateIds = [];
      this.estimateRequestId = 0;
      this.estimateError = '';
      this.manualMode = false;
      this.catalogLoading = false;
      this.conditionRequirements = [];
      this.aestheticGrades = [];
      this.resetConditionAnswers();
      this.highestCatalogValue = 0;
      this.modalOpen = false;
      this.previousBodyOverflow = '';

      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleImeiInput = this.handleImeiInput.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleNativeClick = this.handleNativeClick.bind(this);
      this.handleNativeChange = this.handleNativeChange.bind(this);

      injectStyles();

      if (this.isSelfContainedPlacement) {
        this.addEventListener('click', this.handleClick);
        this.addEventListener('change', this.handleChange);
        this.addEventListener('input', this.handleImeiInput);
      } else {
        this.hidden = true;
        this.style.display = 'none';
        document.addEventListener('click', this.handleNativeClick);
        document.addEventListener('change', this.handleNativeChange);
        document.addEventListener('input', this.handleImeiInput);
      }
      document.addEventListener('keydown', this.handleKeydown);

      this.renderLoadingState();
      this.loadCatalog();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('change', this.handleChange);
      this.removeEventListener('input', this.handleImeiInput);
      document.removeEventListener('click', this.handleNativeClick);
      document.removeEventListener('change', this.handleNativeChange);
      document.removeEventListener('input', this.handleImeiInput);
      document.removeEventListener('keydown', this.handleKeydown);
      this.abortController?.abort();
      this.unlockScroll();
    }

    get config() {
      return {
        placement: this.getPlacement(),
        catalogUrl: CATALOG_URL,
        lookupUrl: LOOKUP_URL,
        estimateUrl: ESTIMATE_URL,
        sellUrl: this.getAttribute('sell-url') || DEFAULT_SELL_URL,
        purchaseAmount: this.getAttribute('data-purchase-amount') || this.getAttribute('purchase-amount') || '',
        locale: this.getAttribute('locale') || document.documentElement.lang || 'pl-PL',
        modalTitle: this.getAttribute('modal-title') || 'Wymień swoje urządzenie na nowe',
        description: this.getAttribute('description') || 'Wybierz urządzenie, które chcesz wymienić:',
        fallbackDescription:
          this.getAttribute('data-fallback-description') ||
          this.getAttribute('fallback-description') ||
          DEFAULT_FALLBACK_DESCRIPTION,
        selectLabel: this.getAttribute('select-label') || 'Model:',
        valueTitle: this.getAttribute('value-title') || 'Szacunkowa wartość skupu',
        valueDescription:
          this.getAttribute('value-description') ||
          'Cena orientacyjna. Ostateczna wycena po ocenie urządzenia przez Dipli SAS.',
        disclaimer: this.getAttribute('disclaimer') || '',
        loadingText: this.getAttribute('loading-text') || 'Ładowanie opcji odkupu...',
        errorText:
          this.getAttribute('error-text') || 'Opcje odkupu są teraz niedostępne. Możesz kontynuować bez odkupu.',
        triggerClass: this.getAttribute('trigger-class') || 'button button--primary dipli-trade-in-landing-trigger',
        startText: this.getAttribute('start-text') || 'Rozpocznij odkup',
        editText: this.getAttribute('edit-text') || 'Edytuj odkup',
        noText: this.getAttribute('no-text') || 'Bez odkupu',
        addText: this.getAttribute('add-text') || 'Odsprzedaj',
        learnMoreText: this.getAttribute('learn-more-text') || 'Dowiedz się więcej',
        learnMoreUrl: this.getAttribute('learn-more-url') || '',
        cartTitle: this.getAttribute('cart-title') || 'Odkup urządzenia',
        cartDescription: this.getAttribute('cart-description') || 'Wybierz urządzenie i sprawdź szacunkową wartość.',
        categoryLabel: this.getAttribute('category-label') || 'Kategoria urządzenia:',
        brandLabel: this.getAttribute('brand-label') || 'Marka:',
        imeiQuestion: this.getAttribute('imei-question') || 'Znasz IMEI swojego urządzenia?',
        imeiPlaceholder: this.getAttribute('imei-placeholder') || 'np. 352923117158722',
        imeiSearchText: this.getAttribute('imei-search-text') || 'Szukaj',
        manualText: this.getAttribute('manual-text') || 'Nie znam IMEI — wybiorę ręcznie',
        invalidImeiText: this.getAttribute('invalid-imei-text') || 'Wpisz poprawny IMEI',
        imeiNotFoundText:
          this.getAttribute('imei-not-found-text') || 'Nie znaleziono urządzenia. Wybierz model ręcznie.',
        imeiAmbiguousText:
          this.getAttribute('imei-ambiguous-text') || 'Znaleziono kilka modeli. Wybierz właściwy model.',
        turnsOnQuestion: this.getAttribute('turns-on-question') || 'Czy urządzenie się włącza?',
        screenGoodQuestion: this.getAttribute('screen-good-question') || 'Czy ekran jest w dobrym stanie?',
        hasFaultsQuestion: this.getAttribute('has-faults-question') || 'Czy urządzenie posiada jakieś usterki?',
        aestheticQuestion: this.getAttribute('aesthetic-question') || 'Jaki jest stan estetyczny urządzenia?',
        aestheticPlaceholder: this.getAttribute('aesthetic-placeholder') || 'Oceń stan urządzenia:',
        conditionTitle: this.getAttribute('condition-title') || 'Oceń stan urządzenia:',
        resetText: this.getAttribute('reset-text') || 'Zacznij od nowa',
      };
    }

    getPlacement() {
      const placement =
        this.getAttribute('placement') || this.getAttribute('layout') || this.dataset.dipliPlacement || '';
      const wrapperPlacement = this.closest('[data-dipli-placement]')?.dataset.dipliPlacement || '';

      if (wrapperPlacement === 'cart-native') return wrapperPlacement;
      if (placement) return placement;
      if (this.closest('[data-dipli-cart-line]')) return 'cart-line';
      if (this.closest('.cto-trade-in')) return 'cto-pdp';

      return 'pdp';
    }

    get isCartLinePlacement() {
      return this.config.placement === 'cart-line';
    }

    get isLandingPagePlacement() {
      return this.config.placement === 'landing-page';
    }

    get isSelfContainedPlacement() {
      return this.isCartLinePlacement || this.isLandingPagePlacement;
    }

    get isNativeCartPlacement() {
      return this.config.placement === 'cart-native';
    }

    get selectedValue() {
      return firstMoneyValue(this.selectedPrice?.value, this.selectedProduct?.value, '0') || '0';
    }

    get selectedDisplayValue() {
      return formatMoney(this.selectedValue, this.displayCurrency, this.config.locale);
    }

    get displayCurrency() {
      return this.catalogCurrency || DEFAULT_CURRENCY;
    }

    get canCommitTradeIn() {
      return Boolean(this.selectedProduct && this.selectedPrice);
    }

    get cartLineContext() {
      const wrapper = this.closest('[data-dipli-cart-line]');
      const properties = parseProperties(wrapper?.dataset.itemProperties || '{}');

      return {
        key: wrapper?.dataset.cartKey || '',
        line: Number.parseInt(wrapper?.dataset.cartLine || '', 10),
        quantity: Number.parseInt(wrapper?.dataset.cartQuantity || '', 10) || 1,
        properties,
      };
    }

    async loadCatalog() {
      const { catalogUrl } = this.config;

      if (!catalogUrl) {
        this.renderUnavailableState();
        return;
      }

      try {
        this.catalogLoading = true;
        const payload = await this.fetchCatalog(catalogUrl);

        this.catalogCategories = getCatalogCategories(payload);
        this.products = flattenCatalogModels(payload);
        this.catalogVersion = String(payload?.meta?.catalogVersion || '');
        this.catalogCurrency = String(payload?.meta?.currency || DEFAULT_CURRENCY);
        this.conditionRequirements = getCatalogConditionRequirements(payload);
        this.aestheticGrades = getCatalogAestheticGrades(payload);
        this.highestCatalogValue = getHighestCatalogValue(payload);
        this.initializeFilters();

        if (
          !this.catalogVersion ||
          !this.catalogCategories.length ||
          !this.products.length ||
          !this.conditionRequirements.length ||
          !this.aestheticGrades.length
        ) {
          this.renderUnavailableState();
          return;
        }

        this.renderReadyState();
      } catch (error) {
        if (error.name === 'AbortError') return;

        console.warn('[Dipli trade-in] Catalog unavailable:', error);
        this.renderUnavailableState();
      } finally {
        this.catalogLoading = false;
      }
    }

    async fetchCatalog(url) {
      return this.fetchJson(url);
    }

    async fetchJson(url, init = {}) {
      const response = await fetch(url, {
        credentials: 'same-origin',
        signal: this.abortController.signal,
        ...init,
        headers: {
          Accept: 'application/json',
          ...init.headers,
        },
      });

      if (!response.ok) {
        let payload = null;

        try {
          payload = await response.json();
        } catch {
          // The status is enough to classify the failure when the proxy has no JSON body.
        }

        throw new CatalogRequestError(response.status, payload);
      }

      return response.json();
    }

    renderLoadingState() {
      if (!this.isSelfContainedPlacement) {
        this.hydrateNativeState(this.config.loadingText, true);
        return;
      }

      this.innerHTML = this.renderSelfContainedPlacement(true);
    }

    renderUnavailableState() {
      this.products = [];
      this.catalogCategories = [];
      this.catalogVersion = '';
      this.conditionRequirements = [];
      this.aestheticGrades = [];
      this.highestCatalogValue = 0;
      this.prepareInitialFlow();
      if (!this.isSelfContainedPlacement) {
        this.hydrateNativeState(this.config.errorText, true);
        this.syncNativeDescription();
        return;
      }

      this.innerHTML = this.renderSelfContainedPlacement(false, true);
    }

    renderReadyState() {
      if (!this.isSelfContainedPlacement) {
        this.hydrateNativeState('', false);
        this.syncNativeDescription();
        return;
      }

      this.innerHTML = this.renderSelfContainedPlacement();
      if (this.isCartLinePlacement) this.hydrateExistingCartSelection();
    }

    initializeFilters() {
      const categories = this.categoryOptions;

      if (!this.selectedCategory && categories.length === 1) {
        this.selectedCategory = categories[0].value;
      }

      if (!categories.some((category) => category.value === this.selectedCategory)) {
        this.selectedCategory = '';
      }

      const brands = this.brandOptions;

      if (!brands.some((brand) => brand.value === this.selectedBrand)) {
        this.selectedBrand = '';
      }

      if (this.selectedProduct && !this.modelOptions.some((product) => product.id === this.selectedProduct.id)) {
        this.selectedProduct = null;
        this.resetConditionAnswers();
      }
    }

    get categoryOptions() {
      return (this.catalogCategories || []).map((category) => ({
        value: String(category.key),
        label: normalizeText(category.label),
      }));
    }

    get categoryProducts() {
      if (!this.selectedCategory) return this.products;

      return this.products.filter((product) => product.categoryKey === this.selectedCategory);
    }

    get brandOptions() {
      const category = (this.catalogCategories || []).find((item) => String(item.key) === this.selectedCategory);

      return (Array.isArray(category?.brands) ? category.brands : []).map((brand) => ({
        value: String(brand.key),
        label: normalizeText(brand.label),
      }));
    }

    get filteredProducts() {
      return this.categoryProducts.filter((product) => !this.selectedBrand || product.brandKey === this.selectedBrand);
    }

    get modelOptions() {
      const candidateIds = new Set(this.lookupCandidateIds);

      return sortByLabel(
        this.filteredProducts
          .filter((product) => !candidateIds.size || candidateIds.has(product.id))
          .map((product) => ({
            ...product,
            value: product.id,
          })),
      );
    }

    getEmptyConditionAnswers() {
      return {
        turnsOn: null,
        screenGood: null,
        hasFaults: null,
        aestheticGrade: '',
      };
    }

    resetConditionAnswers() {
      this.estimateRequestId += 1;
      this.conditionAnswers = this.getEmptyConditionAnswers();
      this.selectedPrice = null;
      this.estimateError = '';
    }

    prepareInitialFlow() {
      this.imeiLookupId += 1;
      this.selectedProduct = null;
      this.selectedCategory = '';
      this.selectedBrand = '';
      this.entryMode = 'imei';
      this.flowStage = FLOW_STAGE_INITIAL;
      this.openDropdown = '';
      this.imeiValue = '';
      this.imeiError = '';
      this.lookupCandidateIds = [];
      this.manualMode = false;
      this.resetConditionAnswers();
      this.initializeFilters();
    }

    get nativeShell() {
      return (
        this.closest('[data-dipli-trade-in-shell]') ||
        this.closest('.trade-in')?.querySelector('[data-dipli-trade-in-shell]') ||
        document.querySelector('[data-dipli-trade-in-shell]')
      );
    }

    get nativeRoot() {
      return this.nativeShell?.closest('.trade-in') || this.nativeShell || this.closest('.trade-in') || document;
    }

    get nativeModalContent() {
      return (
        this.nativeRoot?.querySelector('[data-dipli-trade-in-modal]') ||
        document.querySelector('[data-dipli-trade-in-modal]')
      );
    }

    get nativeCommitButton() {
      return this.nativeModalContent?.querySelector('[data-dipli-trade-in-commit]');
    }

    get hasCommittedNativeSelection() {
      const deviceInput = this.nativeShell?.querySelector('#selected-trade-in-device');
      const valueInput = this.nativeShell?.querySelector('#selected-trade-in-value');

      return Boolean(normalizeText(deviceInput?.value) && Number.parseFloat(toMoneyValue(valueInput?.value)) > 0);
    }

    getNativeShellText(key, fallback) {
      return this.nativeShell?.dataset?.[key] || fallback;
    }

    isNativeTarget(target) {
      return Boolean(
        (this.nativeShell && this.nativeShell.contains(target)) ||
        (this.nativeModalContent && this.nativeModalContent.contains(target)),
      );
    }

    hydrateNativeState(message = '', disabled = false) {
      if (!this.nativeModalContent) return;

      this.nativeModalContent.innerHTML = this.renderTradeInFormContent({
        disabled,
        message,
      });
    }

    syncNativeDescription() {
      const fallback =
        this.getAttribute('data-fallback-description') ||
        this.getAttribute('fallback-description') ||
        this.config?.fallbackDescription ||
        DEFAULT_FALLBACK_DESCRIPTION;

      const syncDescription = (element) => {
        const template =
          element.dataset?.dipliTradeInDescription ||
          element.getAttribute?.('data-dipli-trade-in-description') ||
          element.textContent;

        const elementFallback =
          element.dataset?.fallbackDescription || element.getAttribute?.('data-fallback-description') || fallback;

        const hasPlaceholder = hasHighestValueTemplate(template);
        const numericValue = Number.parseFloat(toMoneyValue(this.highestCatalogValue));
        const hasValidValue = Number.isFinite(numericValue) && numericValue > 0;

        if (hasPlaceholder && !hasValidValue) {
          element.textContent = elementFallback;
          element.hidden = false;
          return;
        }

        element.textContent = renderHighestValueTemplate(
          template,
          this.highestCatalogValue,
          this.displayCurrency,
          this.config.locale,
          elementFallback,
        );
        element.hidden = false;
      };

      this.nativeRoot?.querySelectorAll('[data-dipli-trade-in-description]').forEach(syncDescription);

      const modalDescription = this.nativeModalContent?.querySelector('.description');
      if (modalDescription) syncDescription(modalDescription);
    }

    syncNativeMessage(message) {
      const shell = this.nativeShell;
      if (!shell) return;

      let messageElement = shell.querySelector('[data-dipli-trade-in-message]');
      if (!message) {
        messageElement?.remove();
        return;
      }

      if (!messageElement) {
        messageElement = document.createElement('p');
        messageElement.className = 'dipli-trade-in__message apl-section-trade-in-form-message';
        messageElement.dataset.dipliTradeInMessage = '';
        shell.querySelector('.js-product-option-form')?.after(messageElement);
      }

      messageElement.textContent = message;
    }

    syncNativeValue() {
      const value = this.canCommitTradeIn ? this.selectedValue : String(this.highestCatalogValue || 0);
      const displayValue = this.canCommitTradeIn ? formatMoney(value, this.displayCurrency, this.config.locale) : '';

      const valueElements = new Set();
      [this.nativeRoot, this.nativeModalContent].filter(Boolean).forEach((scope) => {
        scope.querySelectorAll('[data-dipli-trade-in-value], .js-trade-in-value-amount').forEach((element) => {
          valueElements.add(element);
        });
      });

      valueElements.forEach((element) => {
        if (displayValue) element.textContent = displayValue;
        element.setAttribute('data-trade-in-value', value || '0');
      });

      this.syncNativeValuePanel();
    }

    syncNativeValuePanel() {
      const title = this.nativeModalContent?.querySelector('[data-dipli-value-title]');
      const description = this.nativeModalContent?.querySelector('[data-dipli-value-description]');
      const shouldHide = !this.canCommitTradeIn;

      if (title) title.hidden = shouldHide;
      if (description) description.hidden = shouldHide;
    }

    syncNativeCommitButton(disabled = false) {
      const button = this.nativeCommitButton;
      if (!button) return;

      button.disabled = disabled || !this.canCommitTradeIn;
      button.dataset.tradeInValue = this.isNativeCartPlacement
        ? toCartButtonValue(this.selectedValue)
        : this.selectedValue;
      button.dataset.tradeInDevice = this.selectedProduct?.label || '';
      button.dataset.purchaseAmount = this.config.purchaseAmount;
    }

    handleNativeClick(event) {
      const target = event.target instanceof HTMLElement ? event.target : event.target?.parentElement;
      if (!target || !this.isNativeTarget(target)) return;

      const nativeOpenTarget = target.closest('.js-trade-in-modal-trigger-input');
      if (nativeOpenTarget && !this.hasCommittedNativeSelection) {
        this.prepareInitialFlow();
        this.hydrateNativeState('', false);
      }

      if (this.handleTradeInFlowClick(event, target)) return;

      if (target.closest('[data-dipli-trade-in-commit]')) {
        this.openSellFlow();
        if (this.isNativeCartPlacement) return;

        event.preventDefault();
        this.commitNativeSelection();
      }
    }

    handleNativeChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLElement) || !this.isNativeTarget(target)) return;

      if (this.handleTradeInFlowChange(target, true)) return;

      if (target.matches('[data-trade-in="no-trade-in"]')) {
        this.clearNativeSelection();
      }
    }

    handleTradeInFlowClick(event, target) {
      const manualTarget = target.closest('[data-dipli-manual-entry]');
      if (manualTarget) {
        event.preventDefault();
        this.showManualFlow();
        return true;
      }

      const searchTarget = target.closest('[data-dipli-imei-search]');
      if (searchTarget) {
        event.preventDefault();
        this.searchImei().catch((error) => this.handleActionError(error));
        return true;
      }

      const resetTarget = target.closest('[data-dipli-reset-flow]');
      if (resetTarget) {
        event.preventDefault();
        this.resetFlow();
        return true;
      }

      const dropdownOptionTarget = target.closest('[data-dipli-dropdown-option]');
      if (dropdownOptionTarget) {
        event.preventDefault();
        this.selectDropdownOption(dropdownOptionTarget);
        return true;
      }

      const dropdownTriggerTarget = target.closest('[data-dipli-dropdown-trigger]');
      if (dropdownTriggerTarget) {
        event.preventDefault();
        this.toggleDropdown(dropdownTriggerTarget.dataset.dipliDropdownName);
        return true;
      }

      const categoryTarget = target.closest('[data-dipli-category]');
      if (categoryTarget) {
        event.preventDefault();
        this.selectCategory(categoryTarget.dataset.dipliCategory);
        return true;
      }

      const brandTarget = target.closest('[data-dipli-brand]');
      if (brandTarget) {
        event.preventDefault();
        this.selectBrand(brandTarget.dataset.dipliBrand);
        return true;
      }

      return false;
    }

    handleTradeInFlowChange(target, native = false) {
      if (target.matches('[data-dipli-imei-input]')) {
        this.updateImeiValue(target.value);
        return true;
      }

      if (target.matches('[data-dipli-condition-value]')) {
        native ? this.selectNativeConditionAnswer(target) : this.selectConditionAnswer(target);
        return true;
      }

      return false;
    }

    handleImeiInput(event) {
      const target = event.target;
      if (!(target instanceof HTMLElement) || !target.matches('[data-dipli-imei-input]')) return;
      if (!this.isSelfContainedPlacement && !this.isNativeTarget(target)) return;

      this.updateImeiValue(target.value);
    }

    updateImeiValue(value) {
      const nextValue = String(value || '');
      if (this.imeiValue === nextValue) return;

      this.imeiValue = nextValue;
      this.imeiError = '';
      this.imeiLookupId += 1;
      this.clearRenderedImeiAlert();
    }

    clearRenderedImeiAlert() {
      const scope = this.isSelfContainedPlacement ? this : this.nativeModalContent;

      scope?.querySelector?.('.dipli-trade-in-alert')?.remove?.();
    }

    toggleDropdown(name) {
      if (!name) return;

      this.openDropdown = this.openDropdown === name ? '' : name;
      this.hydrateOrRefresh();
    }

    selectDropdownOption(target) {
      const dropdown = target.dataset.dipliDropdownName;
      const value = target.dataset.dipliDropdownValue || '';

      this.openDropdown = '';

      if (dropdown === 'model') {
        this.applyProductSelection(value);
        this.hydrateOrRefresh();
        this.autoScrollToLatestStepOnMobile();
        return;
      }

      if (dropdown === 'aestheticGrade') {
        this.setConditionAnswer('aestheticGrade', value);
        this.hydrateOrRefresh();
        this.autoScrollToLatestStepOnMobile();
      }
    }

    showManualFlow() {
      this.imeiLookupId += 1;

      if (this.catalogLoading || !this.products.length) {
        this.prepareInitialFlow();
        this.hydrateCatalogState(this.catalogLoading);
        return;
      }

      this.manualMode = true;
      this.entryMode = 'manual';
      this.lookupCandidateIds = [];
      this.openDropdown = '';
      this.imeiError = '';
      this.initializeFilters();
      this.flowStage = this.selectedCategory ? FLOW_STAGE_BRAND : FLOW_STAGE_DEVICE;
      this.hydrateOrRefresh();
      this.autoScrollToLatestStepOnMobile();
    }

    hydrateCatalogState(isLoading) {
      const modal = this.isSelfContainedPlacement ? this.querySelector('[data-dipli-modal]') : null;
      const wasOpen = Boolean(modal && !modal.hidden);

      if (this.isSelfContainedPlacement) {
        this.innerHTML = this.renderSelfContainedPlacement(isLoading, !isLoading);
        if (wasOpen) this.openModal();
        return;
      }

      this.hydrateNativeState(isLoading ? this.config.loadingText : this.config.errorText, true);
    }

    resetFlow() {
      this.prepareInitialFlow();
      this.hydrateOrRefresh();
    }

    selectCategory(category) {
      const selectedCategory = category || '';
      const shouldKeepProduct = this.selectedProduct?.categoryKey === selectedCategory;

      if (
        this.lookupCandidateIds.length &&
        !this.products.some(
          (product) => this.lookupCandidateIds.includes(product.id) && product.categoryKey === selectedCategory,
        )
      ) {
        this.lookupCandidateIds = [];
      }

      this.selectedCategory = selectedCategory;
      this.selectedBrand = '';
      this.openDropdown = '';
      this.flowStage = this.selectedCategory ? FLOW_STAGE_BRAND : FLOW_STAGE_DEVICE;

      if (!shouldKeepProduct) {
        this.selectedProduct = null;
        this.resetConditionAnswers();
      } else {
        this.estimateRequestId += 1;
        this.selectedPrice = null;
        this.estimateError = '';
      }

      this.initializeFilters();
      this.hydrateOrRefresh();
      this.autoScrollToLatestStepOnMobile();
    }

    selectBrand(brand) {
      const selectedBrand = brand || '';

      if (
        this.lookupCandidateIds.length &&
        !this.products.some(
          (product) =>
            this.lookupCandidateIds.includes(product.id) &&
            product.categoryKey === this.selectedCategory &&
            product.brandKey === selectedBrand,
        )
      ) {
        this.lookupCandidateIds = [];
      }

      this.selectedBrand = selectedBrand;
      this.openDropdown = '';
      if (this.selectedProduct && this.selectedProduct.brandKey !== this.selectedBrand) {
        this.selectedProduct = null;
        this.resetConditionAnswers();
      }
      this.flowStage = this.selectedBrand
        ? this.selectedProduct
          ? FLOW_STAGE_CONDITION
          : FLOW_STAGE_MODEL
        : FLOW_STAGE_BRAND;
      this.estimateRequestId += 1;
      this.selectedPrice = null;
      this.estimateError = '';
      this.hydrateOrRefresh();
      this.autoScrollToLatestStepOnMobile();
    }

    async searchImei() {
      this.imeiValue = normalizeText(this.imeiValue);
      const lookupId = this.imeiLookupId + 1;
      this.imeiLookupId = lookupId;

      if (!IMEI_PATTERN.test(this.imeiValue) && !TAC_PATTERN.test(this.imeiValue)) {
        this.imeiError = this.config.invalidImeiText;
        this.hydrateOrRefresh();
        return;
      }

      const identifier = TAC_PATTERN.test(this.imeiValue)
        ? { type: 'tac', value: this.imeiValue }
        : { type: 'imei', value: this.imeiValue };
      let payload;

      try {
        payload = await this.fetchJson(this.config.lookupUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(identifier),
        });
      } catch (error) {
        if (lookupId !== this.imeiLookupId || error?.name === 'AbortError') return;

        this.imeiError =
          error instanceof CatalogRequestError && error.status === 422
            ? this.config.imeiNotFoundText
            : this.config.errorText;
        this.hydrateOrRefresh();
        return;
      }

      if (lookupId !== this.imeiLookupId) return;

      const candidateIds = Array.isArray(payload?.candidates)
        ? payload.candidates.map((candidate) => String(candidate.id || '')).filter(Boolean)
        : [];
      const candidates = this.products.filter((product) => candidateIds.includes(product.id));

      if (!candidates.length) {
        this.imeiError = this.config.imeiNotFoundText;
        this.hydrateOrRefresh();
        return;
      }

      this.lookupCandidateIds = candidates.map((candidate) => candidate.id);
      this.selectedProduct = candidates.length === 1 ? candidates[0] : null;
      this.selectedCategory = candidates.every((candidate) => candidate.categoryKey === candidates[0].categoryKey)
        ? candidates[0].categoryKey
        : '';
      this.selectedBrand =
        this.selectedCategory && candidates.every((candidate) => candidate.brandKey === candidates[0].brandKey)
          ? candidates[0].brandKey
          : '';
      this.openDropdown = '';
      this.manualMode = true;
      this.entryMode = 'imei';
      this.flowStage = this.selectedProduct
        ? FLOW_STAGE_CONDITION
        : this.selectedBrand
          ? FLOW_STAGE_MODEL
          : this.selectedCategory
            ? FLOW_STAGE_BRAND
            : FLOW_STAGE_DEVICE;
      this.imeiError = candidates.length > 1 ? this.config.imeiAmbiguousText : '';
      this.resetConditionAnswers();
      this.hydrateOrRefresh();
      this.autoScrollToLatestStepOnMobile();
    }

    hydrateOrRefresh() {
      if (this.isSelfContainedPlacement) {
        this.refreshModalContent();
      } else {
        this.hydrateNativeState('', false);
      }
    }

    findProductByValue(value) {
      if (!value) return null;

      const modelProducts = this.modelOptions;
      const index = Number.parseInt(value, 10);

      return (
        modelProducts.find((product) => product.id === value) || (Number.isInteger(index) ? modelProducts[index] : null)
      );
    }

    selectNativeConditionAnswer(input) {
      this.setConditionAnswer(input.dataset.dipliConditionQuestion, input.value);
      this.hydrateNativeState('', false);
      this.autoScrollToLatestStepOnMobile();
    }

    commitNativeSelection() {
      if (!this.canCommitTradeIn) return;

      const deviceInput = this.nativeShell?.querySelector('#selected-trade-in-device');
      const valueInput = this.nativeShell?.querySelector('#selected-trade-in-value');
      const startInput = this.nativeShell?.querySelector('.js-trade-in-modal-trigger-input');
      const startLabel = startInput?.nextElementSibling;
      const successMessage = this.nativeShell?.querySelector('.trade-in__pdp-success');
      const ctoSelectedOption = document.querySelector('.cto-trade-in .js-cto-trade-in-selected-option');
      const summaryText = document.querySelector('.js-cto-summary-trade-in');

      if (deviceInput) deviceInput.value = this.selectedProduct.label;
      if (valueInput) valueInput.value = this.selectedValue;
      if (startInput) {
        startInput.checked = true;
        startInput.setAttribute('checked', 'checked');
      }
      if (startLabel) startLabel.textContent = this.getNativeShellText('dipliEditText', this.config.editText);
      successMessage?.classList.remove('hidden');
      if (ctoSelectedOption) ctoSelectedOption.textContent = this.getNativeShellText('dipliSelectedText', 'Trade-in');
      summaryText?.classList.remove('hidden');

      this.syncNativeValue();
      this.closeNativeModal();

      this.dispatchEvent(
        new CustomEvent('deeply-trade-in:selected', {
          bubbles: true,
          detail: {
            product: this.selectedProduct,
            price: this.selectedPrice,
            purchaseAmount: this.config.purchaseAmount,
            value: this.selectedValue,
          },
        }),
      );
    }

    clearNativeSelection() {
      const startInput = this.nativeShell?.querySelector('.js-trade-in-modal-trigger-input');
      const startLabel = startInput?.nextElementSibling;
      const valueInput = this.nativeShell?.querySelector('#selected-trade-in-value');
      const deviceInput = this.nativeShell?.querySelector('#selected-trade-in-device');
      const successMessage = this.nativeShell?.querySelector('.trade-in__pdp-success');
      const ctoSelectedOption = document.querySelector('.cto-trade-in .js-cto-trade-in-selected-option');
      const summaryText = document.querySelector('.js-cto-summary-trade-in');

      this.selectedProduct = null;
      this.resetConditionAnswers();

      if (deviceInput) deviceInput.value = '';
      if (valueInput) valueInput.value = '0';
      if (startInput) {
        startInput.checked = false;
        startInput.removeAttribute('checked');
      }
      if (startLabel) startLabel.textContent = this.getNativeShellText('dipliStartText', this.config.startText);
      successMessage?.classList.add('hidden');
      if (ctoSelectedOption) ctoSelectedOption.textContent = this.getNativeShellText('dipliNoText', this.config.noText);
      summaryText?.classList.add('hidden');

      this.hydrateNativeState('', false);

      this.dispatchEvent(
        new CustomEvent('deeply-trade-in:cleared', {
          bubbles: true,
        }),
      );
    }

    closeNativeModal() {
      const modal = this.nativeModalContent?.closest('.ac-modal') || document.getElementById('modal-trade-in-modal');
      const closeButton =
        modal?.parentElement?.nextElementSibling || modal?.closest('.modal')?.querySelector('.modal-close-button');

      if (closeButton instanceof HTMLElement) {
        closeButton.click();
      }
    }

    renderSelfContainedPlacement(isLoading = false, isUnavailable = false) {
      return this.isLandingPagePlacement
        ? this.renderLandingPagePlacement(isLoading, isUnavailable)
        : this.renderCartPlacement(isLoading, isUnavailable);
    }

    renderLandingPagePlacement(isLoading = false, isUnavailable = false) {
      const disabled = isLoading || isUnavailable ? 'disabled' : '';
      const statusText = isLoading ? this.config.loadingText : isUnavailable ? this.config.errorText : '';

      return `
        <button
          class="${escapeHtml(this.config.triggerClass)}"
          type="button"
          data-dipli-open-modal
          ${disabled}
        >
          ${escapeHtml(this.config.startText)}
        </button>
        ${statusText ? `<span class="visually-hidden" role="status" aria-live="polite">${escapeHtml(statusText)}</span>` : ''}
        ${this.renderModal(disabled)}
      `;
    }

    renderCartPlacement(isLoading = false, isUnavailable = false) {
      const context = this.cartLineContext;
      const hasTradeIn = Boolean(context.properties._tradeIn_device);
      const selectedDevice = this.selectedProduct?.label || context.properties._tradeIn_device || '';
      const selectedValue = hasTradeIn
        ? centsToMoneyValue(context.properties._tradeIn_value || '0') || '0'
        : this.selectedProduct
          ? this.selectedValue
          : '0';
      const selectedDisplay = formatMoney(selectedValue, this.displayCurrency, this.config.locale);
      const disabled = isLoading || isUnavailable ? 'disabled' : '';
      const message = isLoading ? this.config.loadingText : isUnavailable ? this.config.errorText : '';

      return `
        <div class="trade-in__card-products-on-cart applecare-products-on-cart dipli-trade-in-cart-card">
          <div class="trade-in__card-item-title trade-in-card-two apple-care-card-two two-not-added applecare-item-title apl-section-product-title">
            <p class="apple-care-card-title">${escapeHtml(this.config.cartTitle)}</p>
            <p class="spandesc">${escapeHtml(this.config.cartDescription)}</p>
            ${
              hasTradeIn
                ? `<p class="spandesc dipli-trade-in-cart-selection">
                    <span class="trade-in__card-property">Device: </span>
                    <span class="trade-in__card-value">${escapeHtml(selectedDevice)}</span><br>
                    <span class="trade-in__card-property">Value: </span>
                    <span class="trade-in__card-value">-${escapeHtml(selectedDisplay)}</span>
                  </p>`
                : ''
            }
            ${this.renderLearnMoreLink()}
            ${message ? `<p class="dipli-trade-in__message">${escapeHtml(message)}</p>` : ''}
          </div>
          <div class="three-not-added">&nbsp;</div>
          <div class="four-not-added">
            <button
              class="care-btn btn button button--primary trade-in__card-trigger"
              type="button"
              data-dipli-open-modal
              ${disabled}
            >
              ${escapeHtml(hasTradeIn ? this.config.editText : this.config.startText)}
              <i class="fa-solid fa-angle-right billboard_icon"></i>
            </button>
          </div>
        </div>
        ${this.renderModal(disabled)}
      `;
    }

    renderLearnMoreLink() {
      if (!this.config.learnMoreText) return '';

      const href = this.config.learnMoreUrl || '#';
      return `
        <a
          class="trade-in__learn-more-trigger apl-section-trade-in-learn"
          href="${escapeHtml(href)}"
          ${href === '#' ? 'data-dipli-learn-more' : ''}
        >
          <span class="underlined-text">${escapeHtml(this.config.learnMoreText)}</span>
          <i class="fa-solid fa-angle-right billboard_icon"></i>
        </a>
      `;
    }

    renderDescription() {
      const fallback = this.config?.fallbackDescription || DEFAULT_FALLBACK_DESCRIPTION;
      return renderHighestValueTemplate(
        this.config.description,
        this.highestCatalogValue,
        this.displayCurrency,
        this.config.locale,
        fallback,
      );
    }

    renderModal(disabled = '') {
      return `
        <div class="dipli-trade-in-modal" hidden data-dipli-modal>
          <div class="dipli-trade-in-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="${this.uid}-modal-title" tabindex="-1">
            <button class="dipli-trade-in-modal__close" type="button" aria-label="Close" data-dipli-close-modal>
              <i class="fa-solid fa-xmark" aria-hidden="true"></i>
            </button>
            ${this.renderTradeInFormContent({ disabled })}
          </div>
        </div>
      `;
    }

    renderTradeInFormContent({ disabled = '', message = '' } = {}) {
      const shouldShowImeiSection = this.entryMode !== 'manual';
      const shouldShowDeviceFlow = this.shouldShowDeviceFlow();
      const shouldShowCatalogMessage = shouldShowDeviceFlow && !this.categoryOptions.length;
      const catalogMessage = this.catalogLoading ? this.config.loadingText : this.config.errorText;

      return `
        <div class="trade-in__modal-content dipli-trade-in-flow">
          <h2 class="title" id="${this.uid}-modal-title">${escapeHtml(this.config.modalTitle)}</h2>
          <p class="description">${escapeHtml(this.renderDescription())}</p>
          ${shouldShowImeiSection ? this.renderImeiSection(disabled) : ''}
          ${message ? `<p class="dipli-trade-in__message">${escapeHtml(message)}</p>` : ''}
          ${shouldShowCatalogMessage ? `<p class="dipli-trade-in__message">${escapeHtml(catalogMessage)}</p>` : ''}
          ${
            shouldShowDeviceFlow && !shouldShowCatalogMessage
              ? `
                ${this.renderCategorySection(disabled)}
                ${this.shouldShowBrandFlow() ? this.renderBrandSection(disabled) : ''}
                ${this.shouldShowModelFlow() ? this.renderModelSection(disabled) : ''}
                ${this.shouldShowConditionFlow() ? this.renderConditionOptions() : ''}
                ${this.estimateError ? `<p class="dipli-trade-in-alert" role="alert" aria-live="polite">${escapeHtml(this.estimateError)}</p>` : ''}
                ${this.shouldShowConditionFlow() ? this.renderValuePanel() : ''}
              `
              : ''
          }
          ${this.config.disclaimer ? `<p class="disclaimer">${escapeHtml(this.config.disclaimer)}</p>` : ''}
        </div>
      `;
    }

    renderImeiSection(disabled = '') {
      const disabledAttribute = disabled ? 'disabled' : '';

      return `
        <div class="dipli-trade-in-field dipli-trade-in-field--imei">
          <label class="dipli-trade-in-field__label" for="${this.uid}-imei">${escapeHtml(this.config.imeiQuestion)}</label>
          <div class="dipli-trade-in-imei-row">
            <input
              class="dipli-trade-in-input"
              id="${this.uid}-imei"
              type="text"
              inputmode="numeric"
              autocomplete="off"
              value="${escapeHtml(this.imeiValue)}"
              placeholder="${escapeHtml(this.config.imeiPlaceholder)}"
              data-dipli-imei-input
              ${disabledAttribute}
            >
            <button class="dipli-trade-in-primary-action" type="button" data-dipli-imei-search ${disabledAttribute}>
              ${escapeHtml(this.config.imeiSearchText)}
            </button>
          </div>
          <button class="dipli-trade-in-link" type="button" data-dipli-manual-entry ${disabledAttribute}>
            ${escapeHtml(this.config.manualText)}
          </button>
          ${
            this.imeiError
              ? `<p class="dipli-trade-in-alert" role="alert" aria-live="polite">${escapeHtml(this.imeiError)}</p>`
              : ''
          }
        </div>
      `;
    }

    renderCategorySection(disabled = '') {
      return this.renderOptionGroup({
        className: 'dipli-trade-in-categories',
        disabled,
        label: this.config.categoryLabel,
        options: this.categoryOptions,
        selectedValue: this.selectedCategory,
        dataAttribute: 'data-dipli-category',
      });
    }

    renderBrandSection(disabled = '') {
      return this.renderOptionGroup({
        className: 'dipli-trade-in-brands',
        disabled,
        label: this.config.brandLabel,
        options: this.brandOptions,
        selectedValue: this.selectedBrand,
        dataAttribute: 'data-dipli-brand',
      });
    }

    renderOptionGroup({ className, disabled, label, options, selectedValue, dataAttribute }) {
      if (!options.length) return '';

      return `
        <div class="dipli-trade-in-option-group ${className}">
          <p class="dipli-trade-in-field__label">${escapeHtml(label)}</p>
          <div class="dipli-trade-in-chip-list">
            ${options
              .map((option) => {
                const selected = option.value === selectedValue;

                return `
                  <button
                    class="dipli-trade-in-chip${selected ? ' is-selected' : ''}"
                    type="button"
                    ${dataAttribute}="${escapeHtml(option.value)}"
                    ${disabled}
                  >
                    ${escapeHtml(option.label)}
                  </button>
                `;
              })
              .join('')}
          </div>
        </div>
      `;
    }

    renderCustomDropdown({ id, name, dropdownName, placeholder, options, selectedValue, disabled = '' }) {
      const isDisabled = Boolean(disabled) || !options.length;
      const isOpen = !isDisabled && this.openDropdown === dropdownName;
      const selectedOption = options.find((option) => String(option.value) === String(selectedValue));
      const displayLabel = selectedOption?.label || placeholder;

      return `
        <div
          class="dipli-trade-in-dropdown${isOpen ? ' is-open' : ''}${isDisabled ? ' is-disabled' : ''}"
          data-dipli-dropdown
          data-dipli-dropdown-name="${escapeHtml(dropdownName)}"
        >
          <input id="${escapeHtml(id)}" type="hidden" name="${escapeHtml(name)}" value="${escapeHtml(selectedValue)}">
          <button
            class="dipli-trade-in-dropdown__trigger"
            id="${escapeHtml(id)}-trigger"
            type="button"
            aria-haspopup="listbox"
            aria-expanded="${isOpen ? 'true' : 'false'}"
            aria-controls="${escapeHtml(id)}-listbox"
            data-dipli-dropdown-trigger
            data-dipli-dropdown-name="${escapeHtml(dropdownName)}"
            ${isDisabled ? 'disabled' : ''}
          >
            <span class="dipli-trade-in-dropdown__value${selectedOption ? '' : ' is-placeholder'}">
              ${escapeHtml(displayLabel)}
            </span>
            <span class="dipli-trade-in-dropdown__chevron" aria-hidden="true"></span>
          </button>
          ${
            isOpen
              ? `
                <div class="dipli-trade-in-dropdown__list" id="${escapeHtml(id)}-listbox" role="listbox">
                  ${options
                    .map((option) => {
                      const selected = String(option.value) === String(selectedValue);

                      return `
                        <button
                          class="dipli-trade-in-dropdown__option${selected ? ' is-selected' : ''}"
                          type="button"
                          role="option"
                          aria-selected="${selected ? 'true' : 'false'}"
                          data-dipli-dropdown-option
                          data-dipli-dropdown-name="${escapeHtml(dropdownName)}"
                          data-dipli-dropdown-value="${escapeHtml(option.value)}"
                        >
                          ${escapeHtml(option.label)}
                        </button>
                      `;
                    })
                    .join('')}
                </div>
              `
              : ''
          }
        </div>
      `;
    }

    renderModelSection(disabled = '') {
      const selectedValue = this.selectedProduct?.id || '';

      return `
        <div class="dipli-trade-in-field dipli-trade-in-field--model">
          <label class="dipli-trade-in-field__label" for="${this.uid}-select-trigger">${escapeHtml(this.config.selectLabel)}</label>
          <div class="dipli-trade-in-dropdown-wrap">
            ${this.renderCustomDropdown({
              id: `${this.uid}-select`,
              name: `${this.uid}-select`,
              dropdownName: 'model',
              placeholder: SELECT_PLACEHOLDER,
              options: this.modelOptions.map((product) => ({
                label: product.label,
                value: product.id,
              })),
              selectedValue,
              disabled,
            })}
          </div>
        </div>
      `;
    }

    renderConditionOptions() {
      return `
        <div class="radio-options-container dipli-trade-in-condition-flow">
          <p class="dipli-trade-in-condition-flow__title">${escapeHtml(this.config.conditionTitle)}</p>
          <input id="${this.uid}-trade-in-value" type="hidden" name="${this.uid}-trade-in-value" value="${escapeHtml(
            this.selectedValue,
          )}">
          ${this.renderConditionFlowContent()}
        </div>
      `;
    }

    shouldShowDeviceFlow() {
      return this.flowStage !== FLOW_STAGE_INITIAL || this.manualMode || Boolean(this.selectedProduct);
    }

    shouldShowBrandFlow() {
      return (
        this.flowStage === FLOW_STAGE_BRAND ||
        this.flowStage === FLOW_STAGE_MODEL ||
        this.flowStage === FLOW_STAGE_CONDITION
      );
    }

    shouldShowModelFlow() {
      return this.flowStage === FLOW_STAGE_MODEL || this.flowStage === FLOW_STAGE_CONDITION;
    }

    shouldShowConditionFlow() {
      return this.flowStage === FLOW_STAGE_CONDITION || Boolean(this.selectedPrice);
    }

    renderConditionFlowContent() {
      return [
        this.renderBooleanQuestion('turnsOn', this.config.turnsOnQuestion),
        this.shouldShowQuestion('screenGood')
          ? this.renderBooleanQuestion('screenGood', this.config.screenGoodQuestion)
          : '',
        this.shouldShowQuestion('hasFaults')
          ? this.renderBooleanQuestion('hasFaults', this.config.hasFaultsQuestion)
          : '',
        this.shouldShowAestheticQuestion() ? this.renderAestheticQuestion() : '',
      ].join('');
    }

    renderBooleanQuestion(question, label) {
      return this.renderQuestion(question, label, [
        { label: YES_TEXT, value: 'true' },
        { label: NO_TEXT, value: 'false' },
      ]);
    }

    renderAestheticQuestion() {
      return `
        <div class="dipli-trade-in-field dipli-trade-in-field--aesthetic">
          <label class="dipli-trade-in-field__label" for="${this.uid}-aesthetic-trigger">${escapeHtml(
            this.config.aestheticQuestion,
          )}</label>
          ${this.renderCustomDropdown({
            id: `${this.uid}-aesthetic`,
            name: `${this.uid}-aesthetic`,
            dropdownName: 'aestheticGrade',
            placeholder: this.config.aestheticPlaceholder,
            options: this.aestheticGrades.map((grade) => ({
              label: grade.label,
              value: grade.state,
            })),
            selectedValue: this.conditionAnswers.aestheticGrade,
          })}
        </div>
      `;
    }

    renderQuestion(question, label, options) {
      return `
        <div class="dipli-trade-in-question" data-dipli-question="${escapeHtml(question)}">
          <p class="dipli-trade-in-question__label">${escapeHtml(label)}</p>
          <div class="dipli-trade-in-question__options">
            ${options
              .map((option) => {
                const checked = String(this.conditionAnswers[question]) === String(option.value);
                const id = `${this.uid}-${toHandle(question)}-${toHandle(option.value)}`;

                return `
                  <div class="dipli-trade-in-choice${checked ? ' is-selected' : ''}">
                    <input
                      class="dipli-trade-in-choice__input"
                      id="${escapeHtml(id)}"
                      type="radio"
                      name="${this.uid}-${escapeHtml(question)}"
                      value="${escapeHtml(option.value)}"
                      data-dipli-condition-question="${escapeHtml(question)}"
                      data-dipli-condition-value
                      ${checked ? 'checked' : ''}
                    >
                    <label class="dipli-trade-in-choice__label" for="${escapeHtml(id)}">${escapeHtml(option.label)}</label>
                  </div>
                `;
              })
              .join('')}
          </div>
        </div>
      `;
    }

    renderValuePanel() {
      if (!this.canCommitTradeIn) return '';

      return `
        <div class="dipli-trade-in-value-panel">
          <p class="dipli-trade-in-value-panel__title" data-dipli-value-title>${escapeHtml(this.config.valueTitle)}</p>
          <p class="dipli-trade-in-value-panel__amount js-trade-in-value-amount" data-dipli-value-amount data-trade-in-value="${escapeHtml(this.selectedValue)}">
            ${escapeHtml(this.selectedDisplayValue)}
          </p>
          <p class="dipli-trade-in-value-panel__description" data-dipli-value-description>${escapeHtml(this.config.valueDescription)}</p>
          <button
            class="${this.isCartLinePlacement ? 'add-cart-trade-in ' : 'add-to-product js-add-to-product '}dipli-trade-in-primary-action dipli-trade-in-primary-action--large"
            type="button"
            data-dipli-commit
            data-dipli-trade-in-commit
            data-trade-in-value="${escapeHtml(this.selectedValue)}"
            data-trade-in-device="${escapeHtml(this.selectedProduct?.label || '')}"
            data-purchase-amount="${escapeHtml(this.config.purchaseAmount)}"
          >
            ${escapeHtml(this.config.addText)}
          </button>
          ${this.renderLearnMoreLink()}
          <button class="dipli-trade-in-link dipli-trade-in-reset" type="button" data-dipli-reset-flow>
            ${escapeHtml(this.config.resetText)}
          </button>
        </div>
      `;
    }

    shouldShowQuestion(question) {
      return this.isConditionFieldVisible(question);
    }

    isConditionFieldVisible(field) {
      const requirement = this.conditionRequirements.find((item) => item.field === field);

      if (!requirement) return false;

      return Object.entries(requirement.visibleWhen).every(
        ([answer, expectedValue]) => this.conditionAnswers[answer] === expectedValue,
      );
    }

    shouldShowAestheticQuestion() {
      return this.isConditionFieldVisible('aestheticGrade');
    }

    setConditionAnswer(question, rawValue) {
      if (!question || !(question in this.conditionAnswers)) return;

      this.openDropdown = '';

      if (question === 'aestheticGrade') {
        this.conditionAnswers.aestheticGrade = rawValue || '';
      } else {
        this.conditionAnswers[question] = toBoolean(rawValue);
      }

      this.resetHiddenConditionAnswers();

      this.estimateRequestId += 1;
      this.selectedPrice = null;
      this.estimateError = '';

      if (this.isConditionComplete()) {
        void this.requestEstimate();
      }
    }

    resetHiddenConditionAnswers() {
      this.conditionRequirements.forEach(({ field }) => {
        if (this.isConditionFieldVisible(field)) return;

        this.conditionAnswers[field] = field === 'aestheticGrade' ? '' : null;
      });
    }

    isConditionComplete() {
      if (!this.selectedProduct || !this.catalogVersion) return false;

      const visibleRequirements = this.conditionRequirements.filter(({ field }) => this.isConditionFieldVisible(field));

      return (
        visibleRequirements.length > 0 &&
        visibleRequirements.every(({ field }) =>
          field === 'aestheticGrade'
            ? Boolean(this.conditionAnswers[field])
            : typeof this.conditionAnswers[field] === 'boolean',
        )
      );
    }

    async requestEstimate() {
      if (!this.isConditionComplete()) return;

      const requestId = this.estimateRequestId;

      try {
        const estimate = await this.fetchJson(this.config.estimateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            catalogVersion: this.catalogVersion,
            productId: this.selectedProduct.id,
            condition: this.conditionAnswers,
          }),
        });

        if (requestId !== this.estimateRequestId) return;

        this.selectedPrice = {
          id: String(estimate.estimateId || estimate.state || ''),
          state: String(estimate.state || ''),
          label: String(estimate.state || ''),
          value: centsToMoneyValue(estimate.amountMinor),
          amountMinor: Number(estimate.amountMinor),
          estimateId: String(estimate.estimateId || ''),
          expiresAt: String(estimate.expiresAt || ''),
        };
        this.estimateError = '';
      } catch (error) {
        if (requestId !== this.estimateRequestId || error?.name === 'AbortError') return;

        this.selectedPrice = null;
        this.estimateError = this.config.errorText;
      }

      this.hydrateOrRefresh();
      this.autoScrollToLatestStepOnMobile();
    }

    hydrateExistingCartSelection() {
      if (!this.isCartLinePlacement) return;

      const { properties } = this.cartLineContext;
      if (!properties._tradeIn_device) return;

      this.selectedProduct = this.products.find((product) => product.label === properties._tradeIn_device) || {
        id: properties._tradeIn_device,
        label: properties._tradeIn_device,
        value: centsToMoneyValue(properties._tradeIn_value || '0') || '0',
        prices: [],
      };
      this.selectedPrice = null;
      this.innerHTML = this.renderCartPlacement();
    }

    handleClick(event) {
      const target = event.target instanceof HTMLElement ? event.target : event.target?.parentElement;
      if (!target) return;

      const openTarget = target.closest('[data-dipli-open-modal], .js-trade-in-modal-trigger-input');
      if (openTarget && this.contains(openTarget)) {
        event.preventDefault();
        if (!this.cartLineContext.properties._tradeIn_device) {
          this.prepareInitialFlow();
          this.refreshModalContent();
        }
        this.openModal();
        return;
      }

      if (target.closest('[data-dipli-close-modal]')) {
        event.preventDefault();
        this.closeModal();
        return;
      }

      if (target.matches('[data-dipli-modal]')) {
        this.closeModal();
        return;
      }

      if (this.handleTradeInFlowClick(event, target)) return;

      if (target.closest('[data-dipli-commit]')) {
        event.preventDefault();
        this.openSellFlow();
        this.commitSelection().catch((error) => this.handleActionError(error));
        return;
      }

      if (target.closest('[data-dipli-learn-more]')) {
        event.preventDefault();
        const nativeLearnMore = document.querySelector('.js-trade-in-learn-more-modal-trigger');
        if (nativeLearnMore) {
          nativeLearnMore.click();
          return;
        }
        if (!this.isModalOpen) {
          this.prepareInitialFlow();
          this.refreshModalContent();
          this.openModal();
        }
      }
    }

    handleChange(event) {
      if (event.target.matches('[data-trade-in="no-trade-in"]')) {
        this.clearSelection();
        return;
      }

      this.handleTradeInFlowChange(event.target, false);
    }

    handleDropdownKeydown(event, target) {
      if (!target) return false;

      const optionTarget = target.closest('[data-dipli-dropdown-option]');
      if (optionTarget && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        this.selectDropdownOption(optionTarget);
        return true;
      }

      const triggerTarget = target.closest('[data-dipli-dropdown-trigger]');
      if (triggerTarget && (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown')) {
        event.preventDefault();
        this.toggleDropdown(triggerTarget.dataset.dipliDropdownName);
        return true;
      }

      if (event.key === 'Escape' && this.openDropdown) {
        event.preventDefault();
        this.openDropdown = '';
        this.hydrateOrRefresh();
        return true;
      }

      return false;
    }

    handleKeydown(event) {
      const target = event.target instanceof HTMLElement ? event.target : event.target?.parentElement;
      if (this.handleDropdownKeydown(event, target)) return;

      if (event.key === 'Escape' && this.modalOpen) {
        this.closeModal();
      }
    }

    applyProductSelection(indexValue) {
      const product = this.findProductByValue(indexValue);

      this.selectedProduct = product;
      this.openDropdown = '';

      if (!product) {
        this.resetConditionAnswers();
        this.flowStage = this.selectedBrand
          ? FLOW_STAGE_MODEL
          : this.selectedCategory
            ? FLOW_STAGE_BRAND
            : FLOW_STAGE_DEVICE;
        return;
      }

      this.selectedCategory = product.categoryKey || this.selectedCategory;
      this.selectedBrand = product.brandKey || this.selectedBrand;
      this.flowStage = FLOW_STAGE_CONDITION;
      this.resetConditionAnswers();
    }

    selectConditionAnswer(input) {
      this.setConditionAnswer(input.dataset.dipliConditionQuestion, input.value);
      this.refreshModalContent();
      this.autoScrollToLatestStepOnMobile();
    }

    autoScrollToLatestStepOnMobile() {
      const isMobile =
        typeof window !== 'undefined' && typeof window.matchMedia === 'function'
          ? window.matchMedia('(max-width: 767px)').matches
          : typeof window !== 'undefined' && typeof window.innerWidth === 'number' && window.innerWidth <= 767;

      if (!isMobile) return;

      const performScroll = () => {
        const scope = this.nativeModalContent || this;
        if (!scope || typeof scope.querySelector !== 'function') return;

        const targetElement =
          scope.querySelector('.dipli-trade-in-value-panel:not([hidden])') ||
          scope.querySelector('.dipli-trade-in-alert[role="alert"]') ||
          scope.querySelector('.dipli-trade-in-field--aesthetic') ||
          scope.querySelector('.dipli-trade-in-condition-flow .dipli-trade-in-question:last-of-type') ||
          scope.querySelector('.dipli-trade-in-condition-flow') ||
          scope.querySelector('.dipli-trade-in-field--model') ||
          scope.querySelector('.dipli-trade-in-brands') ||
          scope.querySelector('.dipli-trade-in-categories');

        if (!targetElement) return;

        if (typeof targetElement.scrollIntoView === 'function') {
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        } else {
          const scrollContainer =
            this.nativeModalContent?.closest?.('.modal') || this.querySelector?.('[data-dipli-modal]');
          if (scrollContainer && typeof scrollContainer.scrollTo === 'function') {
            scrollContainer.scrollTo({
              top: scrollContainer.scrollHeight,
              behavior: 'smooth',
            });
          }
        }
      };

      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(performScroll);
      } else {
        performScroll();
      }
    }

    refreshModalContent() {
      const modal = this.querySelector('[data-dipli-modal]');
      const wasOpen = Boolean(modal && !modal.hidden);
      const scrollTop = modal?.scrollTop || 0;

      if (!this.isSelfContainedPlacement) return;

      this.innerHTML = this.renderSelfContainedPlacement();

      if (wasOpen) {
        this.openModal();
        const nextModal = this.querySelector('[data-dipli-modal]');
        if (nextModal) nextModal.scrollTop = scrollTop;
      }
    }

    openModal() {
      const modal = this.querySelector('[data-dipli-modal]');
      const dialog = modal?.querySelector('.dipli-trade-in-modal__dialog');

      if (!modal || !dialog) return;

      modal.hidden = false;
      this.modalOpen = true;
      this.lockScroll();
      document.addEventListener('keydown', this.handleKeydown);
      window.requestAnimationFrame(() => dialog.focus());
    }

    closeModal() {
      const modal = this.querySelector('[data-dipli-modal]');

      if (!modal) return;

      modal.hidden = true;
      this.modalOpen = false;
      this.unlockScroll();
      document.removeEventListener('keydown', this.handleKeydown);
    }

    lockScroll() {
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    unlockScroll() {
      if (this.previousBodyOverflow !== undefined) {
        document.body.style.overflow = this.previousBodyOverflow;
      }
    }

    async commitSelection() {
      if (!this.canCommitTradeIn) return;

      if (!this.isCartLinePlacement) return;

      await this.commitCartSelection();
    }

    openSellFlow() {
      if (!this.config.sellUrl) return;

      window.open(this.config.sellUrl, '_blank', 'noopener,noreferrer');
    }

    async commitCartSelection() {
      const context = this.cartLineContext;
      const properties = { ...context.properties };
      const payload = {
        quantity: context.quantity,
        properties,
      };

      properties._tradeInEligible = 'true';
      properties._tradeIn_value = toCartPropertyCents(this.selectedValue);
      properties._tradeIn_device = this.selectedProduct.label;
      properties._tradeIn_estimateId = this.selectedPrice.estimateId || '';
      properties._tradeIn_catalogVersion = this.catalogVersion;

      if (properties._uuid && !properties._uuid.includes('tradein')) {
        properties._uuid = `${properties._uuid}-tradein`;
      }

      if (context.key) {
        payload.id = context.key;
      } else if (Number.isInteger(context.line)) {
        payload.line = context.line;
      }

      await this.updateCartLine(payload);
      this.closeModal();
      this.refreshCart();
    }

    clearSelection() {
      this.selectedProduct = null;
      this.resetConditionAnswers();

      if (!this.isCartLinePlacement) return;

      this.clearCartSelection().catch((error) => this.handleActionError(error));
    }

    async clearCartSelection() {
      const context = this.cartLineContext;

      if (!context.properties._tradeIn_device) {
        this.innerHTML = this.renderCartPlacement();
        return;
      }

      const properties = { ...context.properties };
      const payload = {
        quantity: context.quantity,
        properties,
      };

      delete properties._tradeIn_value;
      delete properties._tradeIn_device;
      delete properties._tradeIn_estimateId;
      delete properties._tradeIn_catalogVersion;

      if (properties._uuid) {
        properties._uuid = properties._uuid.split('-tradein')[0];
      }

      if (context.key) {
        payload.id = context.key;
      } else if (Number.isInteger(context.line)) {
        payload.line = context.line;
      }

      await this.updateCartLine(payload);
      this.refreshCart();
    }

    async updateCartLine(payload) {
      const response = await fetch(`${getRoutesRoot()}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Cart update failed with ${response.status}`);
      }

      return response.json();
    }

    handleActionError(error) {
      console.warn('[Dipli trade-in] Action failed:', error);
    }

    refreshCart() {
      if (typeof window.onCartUpdate === 'function') {
        window.onCartUpdate();
        return;
      }

      window.location.reload();
    }
  }

  if (!customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, DeeplyTradeInPlacement);
  }
})();
